import { useState } from "react";
import { Users, UserPlus, Mail, Lock, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";
import { initializeApp, getApps } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { firebaseConfig } from "../../firebase";

export function UserManagement() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Hardcoded mock list for hackathon demo
  const mockStaff = [
    { email: "ops@hospital.org", role: "Admin / Ops Lead", created: "Initial Setup" },
    { email: "dr.chen@hospital.org", role: "Clinical Lead", created: "Aug 28, 2024" },
    { email: "nursing.desk@hospital.org", role: "Ward Manager", created: "Aug 29, 2024" },
  ];

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Create secondary app to avoid logging out the current admin
      const SECONDARY_APP_NAME = "SecondaryUserCreationApp";
      const apps = getApps();
      const existingApp = apps.find(a => a.name === SECONDARY_APP_NAME);
      
      const secondaryApp = existingApp || initializeApp(firebaseConfig, SECONDARY_APP_NAME);
      const secondaryAuth = getAuth(secondaryApp);

      // Create the user
      await createUserWithEmailAndPassword(secondaryAuth, email, password);
      
      // Sign out the secondary instance to clean up
      await signOut(secondaryAuth);

      setSuccess(`Successfully created account for ${email}`);
      setEmail("");
      setPassword("");
    } catch (err: any) {
      setError(err.message || "Failed to create user.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      <div
        className="rounded-2xl border border-teal-500/20 p-5 backdrop-blur-md"
        style={{ background: "rgba(20, 184, 166, 0.05)" }}
      >
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
          <div>
            <h2 className="text-white font-semibold text-sm">Staff Account Management</h2>
            <p className="text-gray-400 text-sm mt-1 leading-relaxed">
              As an Operations Lead, you can provision access for other staff members. 
              Accounts created here can log into HospOps Central to view the operational dashboard.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Create User Form */}
        <div className="lg:col-span-1">
          <div
            className="rounded-2xl border border-white/8 p-6"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
            }}
          >
            <div className="flex items-center gap-2 mb-6">
              <UserPlus className="w-4 h-4 text-teal-400" />
              <h2 className="text-white font-semibold text-base">Provision New Account</h2>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="staff@hospital.org"
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                  Temporary Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-300">{error}</p>
                </div>
              )}

              {success && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                  <ShieldCheck className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-green-300">{success}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 px-4 mt-2 rounded-xl bg-gradient-teal text-white font-semibold text-sm hover:shadow-glow active:scale-[0.99] transition-all disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin h-4 w-4" />
                    Creating...
                  </span>
                ) : (
                  "Create Account"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Directory (Mock) */}
        <div className="lg:col-span-2">
          <div
            className="rounded-2xl border border-white/8 overflow-hidden h-full"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
            }}
          >
            <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-teal-400" />
                <h2 className="text-white font-semibold text-base">Active Staff Directory</h2>
              </div>
              <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded-full border border-white/10">
                Demo Directory
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Level</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provisioned</th>
                  </tr>
                </thead>
                <tbody>
                  {mockStaff.map((s, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/3">
                      <td className="px-6 py-4 text-white font-medium">{s.email}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2 py-1 rounded-md text-xs border border-white/10 bg-white/5 text-gray-300">
                          {s.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-xs">{s.created}</td>
                    </tr>
                  ))}
                  {success && (
                    <tr className="border-b border-white/5 bg-teal-500/10 animate-fade-in">
                      <td className="px-6 py-4 text-teal-300 font-medium">{success.replace("Successfully created account for ", "")}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2 py-1 rounded-md text-xs border border-teal-500/20 bg-teal-500/10 text-teal-300">
                          New User
                        </span>
                      </td>
                      <td className="px-6 py-4 text-teal-500/70 text-xs">Just now</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
